import { NgClass, NgComponentOutlet } from '@angular/common'
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ComponentRef,
  ElementRef,
  EventEmitter,
  HostListener,
  Injector,
  Input,
  OnInit,
  Output,
  ViewChild,
  ViewContainerRef
} from '@angular/core'
import { SafeDomPipe } from '@app/v2/shared/pipes/safe-dom.pipe'
import { TranslateModule, TranslatePipe } from '@ngx-translate/core'
import { Subject } from 'rxjs'
import { ComponentWithDataSetter, DropdownOption } from '../dropdown/types/dropdown-option.type'

@Component({
  selector: 'app-dropdown-content',
  templateUrl: './dropdown-content.component.html',
  styleUrls: ['./dropdown-content.component.scss'],
  standalone: true,
  imports: [NgClass, NgComponentOutlet, SafeDomPipe, TranslateModule, TranslatePipe]
})
export class DropdownContentComponent implements OnInit, AfterViewInit {
  @Input() hiddenOption?: DropdownOption
  //pass an array of options to the dropdown
  @Input() dropdownOptions?: DropdownOption[] = []
  //pass a custom component to the dropdown
  @Input() componentWithDataSetter?: ComponentWithDataSetter<any>
  // Width for dropdown
  @Input() widthInPX: string = '187px'
  // Max Width for dropdown
  @Input() minWidthInPX: string = '70px'
  // Height for internal dropdown content
  @Input() heightInPX: number = 120
  // Max Height for internal dropdown content
  @Input() maxHeightInPX: number = 340
  // Optional vertical gap between options (defaults to no extra gap)
  @Input() optionGapInPX: number = 0
  // The flag to handle the style of the dropdowns for a field
  @Input() isField: boolean = false
  // The flag to handle the style of the dropdowns for a field
  @Input() isSingleSelect: boolean = true
  @Input() optionNoData: string = ''
  // Flag to indicate if user has searched for something
  @Input() hasSearchQuery: boolean = false
  // Flag to force hide dropdown content even for field types (for city fields with no options)
  @Input() forceHide: boolean = false
  // The selected option object
  @ViewChild('dynamicTarget', { read: ViewContainerRef, static: true }) target: any
  @ViewChild('dropdownContainer') dropdownContainer!: ElementRef

  @Output() dropdownClosed: EventEmitter<void> = new EventEmitter<void>()
  @Output() optionClicked: EventEmitter<DropdownOption> = new EventEmitter<DropdownOption>()

  public focusedIndex: number = -1
  public containerMaxHeight?: number
  private _customComponent?: ComponentRef<any>
  private _destroy$: Subject<void> = new Subject<void>()

  constructor(private injector: Injector, private _ChangeDetectorRef: ChangeDetectorRef) {}

  ngOnInit() {
    this._createComponent()
  }

  ngAfterViewInit(): void {
    this._createInitialMaxHeight()
    this._ChangeDetectorRef.detectChanges()
  }

  ngOnDestroy(): void {
    this._destroy$.next()
    this._destroy$.complete()
  }

  /**
   * @description Handle the keydown event
   * @param event KeyboardEvent
   */
  @HostListener('document:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (!this.componentWithDataSetter?.component) {
      if (['Shift', 'Tab', 'Escape'].includes(event.key)) {
        event.preventDefault()
        this.dropdownClosed.emit()
      }
    }
    if (!this.displayCustomComponent || !this.dropdownOptions?.length) return
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        this._moveFocusDown()
        break
      case 'ArrowUp':
        event.preventDefault()
        this._moveFocusUp()
        break
      case 'Enter':
        event.preventDefault()
        this._selectFocusedOption()
        break
    }
  }

  /**
   * @description Get the display custom component
   * @returns boolean
   */
  get displayCustomComponent(): boolean {
    return !this.componentWithDataSetter?.component && (!!this.dropdownOptions?.length || this.isField)
  }

  /**
   * @description Create initial max height for custom component calculated from current height
   * @returns void
   */
  private _createInitialMaxHeight(): void {
    const dropdownListContainer = this.dropdownContainer?.nativeElement as HTMLElement | undefined
    this.containerMaxHeight = dropdownListContainer?.getBoundingClientRect().height
  }

  /**
   * @description Create the dropdown component list
   * @returns void
   */
  private _createComponent(): void {
    // Check if the component is defined
    if (!this.componentWithDataSetter?.component) return
    // Clear previous content inside dynamicTarget
    this.target.clear()
    // Create the component inside dynamicTarget ViewContainerRef
    const componentRef = this.target.createComponent(this.componentWithDataSetter.component, {
      injector: this.injector
    })
    // Set the data for the component using the provided data setter
    this.componentWithDataSetter.dataSetter(componentRef.instance)
    // Store the component reference for potential further use
    this._customComponent = componentRef
  }

  /**
   * @description emit the selected option when click on the option
   * @param option DropdownOption
   */
  public onOptionClicked(index: number): void {
    if (this.isField) {
      const isDisabled = this.dropdownOptions?.[index]?.disabled
      if (this.isSingleSelect && isDisabled) return
      if (this.isSingleSelect && this.isField) {
        this.dropdownOptions?.forEach((option) => (option.disabled = false))
      }
      if (this.dropdownOptions?.[index]) {
        if (this.isSingleSelect) this.dropdownOptions[index].disabled = true
        else this.dropdownOptions[index].disabled = !isDisabled
      }
    }
    this.optionClicked.emit(this.dropdownOptions?.[index])
  }

  /**
   * @description Move the focus down
   */
  private _moveFocusDown(): void {
    const lastIndex = (this.dropdownOptions?.length || 0) - 1
    if (this.focusedIndex < lastIndex) this.focusedIndex++
    else if (this.focusedIndex === -1) this.focusedIndex = 0
    this._scrollToFocusedOption()
  }

  /**
   * @description Move the focus up
   */
  private _moveFocusUp(): void {
    if (this.focusedIndex > 0) this.focusedIndex--
    else if (this.focusedIndex === -1) this.focusedIndex = (this.dropdownOptions?.length || 0) - 1
    this._scrollToFocusedOption()
  }

  /**
   * @description Select the focused option
   */
  private _selectFocusedOption(): void {
    if (this.focusedIndex < 0 || !this.dropdownOptions?.[this.focusedIndex]) return
    const option = this.dropdownOptions[this.focusedIndex]
    if (!option.disabled) this.onOptionClicked(this.focusedIndex)
  }

  /**
   * @description Scroll to the focused option
   */
  private _scrollToFocusedOption(): void {
    setTimeout(() => {
      const options = this.dropdownContainer?.nativeElement?.querySelectorAll('.dropdown-option')
      options?.[this.focusedIndex]?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
    })
  }

  /**
   * @description Reset the focus index
   */
  public resetFocusIndex(): void {
    this.focusedIndex = -1
  }
}
